// App.tsx
import React, { useEffect, useState } from "react";
import NavBar from "./NavBar";
import { HelmetProvider } from "react-helmet-async";
import GameDashboard from "../../features/games/dashboard/GameDashboard";
import LoadingComponent from "./LoadingComponent";
import { useStore } from "../stores/store";
import { observer } from "mobx-react-lite";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
import HomePage from "../../features/home/HomePage";
import GameForm from "../../features/games/form/GameForm";
import GameDetails from "../../features/games/details/GameDetails";
import { ToastContainer } from "react-toastify";
import NotFound from "../../features/errors/NotFound";
import { setupInterceptors } from "../api/agent";
import ServerError from "../../features/errors/ServerError";
import ModalContainer from "../common/modals/ModalContainer";
import ProfilePage from "../../features/profiles/ProfilePage";
import LoginPage from "../../features/users/LoginPage";
import { initializeFirebaseMessaging } from "../firebase/firebase-messaging";
import VenueScheduler from "../../features/venues/VenueScheduler";
import { ResetPasswordForm } from "../../features/users/ResetPasswordForm";
import LegalPage from "../../features/legal/LegalPage";
import "../../i18n";
import FindFriends from "../../features/friends/FindFriends";
import Messages from "../../features/messages/Messages";
import PlaygroundPage from "../../features/playground/PlaygroundPage";
import TeamDashboard from "../../features/teams/TeamDashboard";
import TeamDetails from "../../features/teams/TeamDetails";
import TeamForm from "../../features/teams/TeamForm";
import { useTranslation } from "react-i18next";
import TeamSearchPage from "../../features/teams/TeamSearchPage";
import TeamMatchPage from "../../features/teams/TeamMatchPage";
import { TournamentDashboard } from "../../features/tournaments/TournamentDashboard";
import TournamentDetails from "../../features/tournaments/TournamentDetails";
import TournamentForm from "../../features/tournaments/TournamentForm";
import TournamentMatchPage from "../../features/tournaments/TournamentMatchPage";

// Paths that do NOT require authentication.
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/server-error",
  "/account/reset-password",
  "/legal",
];

const isPublicPath = (pathname: string) =>
  PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

// Protected path prefixes used by the deep-link redirect guard.
const PROTECTED_PREFIXES = [
  "/games",
  "/profiles",
  "/venues",
  "/friends",
  "/messages",
  "/playground",
  "/teams",
  "/teammatches",
  "/tournaments",
  "/tournamentmatches",
];

const isProtectedPath = (pathname: string) =>
  PROTECTED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navbarHeight, setNavbarHeight] = useState(0);
  const { gameStore, userStore, commonStore, messageStore, commentStore } =
    useStore();

  // ── Step 1: One-time setup on mount ──────────────────────────────────────
  useEffect(() => {
    userStore.initializeUser();
    userStore.setNavigateFunction(navigate);
    setupInterceptors(navigate);
  }, [userStore, navigate]);

  // ── Step 2: Fetch fresh user data and mark app as loaded ─────────────────
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      commonStore.setAppLoaded();
    }, 5000);

    const init = async () => {
      if (commonStore.token) {
        try {
          await userStore.getUser();
        } catch {
        } finally {
          commonStore.setAppLoaded();
          clearTimeout(safetyTimer);
        }
      } else {
        commonStore.setAppLoaded();
        clearTimeout(safetyTimer);
      }
    };

    init();

    return () => clearTimeout(safetyTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step 3: Load games once the app is ready and the user is logged in ───
  useEffect(() => {
    if (commonStore.appLoaded && commonStore.token) {
      gameStore.loadGames();
    }
  }, [commonStore.appLoaded, commonStore.token, gameStore]);

  // ── Deep-link redirect guard ──────────────────────────────────────────────
  useEffect(() => {
    if (!commonStore.appLoaded) return;
    if (commonStore.token) return;
    if (!isProtectedPath(location.pathname)) return;

    const intendedUrl = location.pathname + location.search;
    userStore.setPendingRedirectUrl(intendedUrl);
    navigate("/login", { state: { from: intendedUrl }, replace: true });
  }, [
    commonStore.appLoaded,
    commonStore.token,
    location.pathname,
    location.search,
    navigate,
    userStore,
  ]);

  // ── SignalR: Messages hub ─────────────────────────────────────────────────
  useEffect(() => {
    const publicPage = isPublicPath(location.pathname);

    if (commonStore.appLoaded && commonStore.token && !publicPage) {
      if (messageStore.hubConnection?.state === "Connected") return;
      const timer = setTimeout(() => {
        messageStore.createHubConnection();
      }, 500);
      return () => clearTimeout(timer);
    } else if (publicPage || !commonStore.token) {
      messageStore.stopHubConnection();
    }
  }, [
    commonStore.appLoaded,
    commonStore.token,
    location.pathname,
    messageStore,
  ]);

  // ── SignalR: Comments hub ─────────────────────────────────────────────────
  useEffect(() => {
    const handleCommentHub = () => {
      const publicPage = isPublicPath(location.pathname);

      const isGameDetailPage =
        location.pathname.startsWith("/games/") &&
        !location.pathname.includes("/manage/") &&
        !location.pathname.includes("/createGame");

      if (
        commonStore.appLoaded &&
        commonStore.token &&
        !publicPage &&
        isGameDetailPage
      ) {
        const gameId = location.pathname.split("/games/")[1];
        if (gameId && !gameId.includes("/")) {
          const timer = setTimeout(() => {
            if (gameStore.selectedGame?.id === gameId)
              commentStore.createHubConnection(gameId);
          }, 300);
          return () => {
            clearTimeout(timer);
            if (commentStore.hubConnection) commentStore.stopHubConnection();
          };
        }
      } else if (commentStore.hubConnection) {
        commentStore.stopHubConnection();
      }
    };
    handleCommentHub();
  }, [
    commonStore.appLoaded,
    commonStore.token,
    location.pathname,
    gameStore.selectedGame?.id,
    commentStore,
  ]);

  // ── SignalR: Messages hub health check ────────────────────────────────────
  useEffect(() => {
    const checkHubHealth = () => {
      if (
        messageStore.hubConnection?.state !== "Connected" &&
        commonStore.token &&
        !location.pathname.match(
          /^(\/|\/login|\/account\/reset-password|\/server-error)$/,
        )
      ) {
        messageStore.createHubConnection();
      }
    };
    checkHubHealth();
    const interval = setInterval(checkHubHealth, 30000);
    return () => clearInterval(interval);
  }, [
    commonStore.token,
    location.pathname,
    gameStore.selectedGame?.id,
    messageStore,
    commentStore,
  ]);

  // ── SignalR: Desktop mode sync ────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const isDesktop = window.innerWidth > 768;
      if (messageStore.hubConnection?.state === "Connected")
        messageStore.checkAndUpdateDesktopMode(isDesktop);
    };
    if (commonStore.token) check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [commonStore.token, messageStore]);

  // ── SignalR: Cleanup on unmount ───────────────────────────────────────────
  useEffect(() => {
    return () => {
      messageStore.stopHubConnection();
      commentStore.stopHubConnection();
    };
  }, [messageStore, commentStore]);

  // ── Firebase Cloud Messaging ──────────────────────────────────────────────
  useEffect(() => {
    const initFirebase = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        const token = await initializeFirebaseMessaging();
        if (token)
          console.log("Firebase messaging initialized with token:", token);
        else console.log("Firebase messaging not available");
      } catch (error) {
        console.error("Firebase initialization error (non-critical):", error);
      }
    };
    const timer = setTimeout(initFirebase, 200);
    return () => clearTimeout(timer);
  }, []);

  // ── Service Worker diagnostics ────────────────────────────────────────────
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => {
          regs.forEach((reg) =>
            console.log(
              "Service Worker registered:",
              reg.scope,
              reg.active?.state,
            ),
          );
        })
        .catch((error) => console.log("Service worker check failed:", error));
    }
  }, []);

  // Layout element — NavBar is shown only when the user is logged in.
  // This keeps "/" inside the same layout route (no flicker on Home),
  // while guests on the landing page still see no navbar.
  const isLoggedIn = userStore.isLoggedIn;
  const layoutElement = (
    <>
      {isLoggedIn && <NavBar onHeightChange={setNavbarHeight} />}
      <div
        style={{
          marginTop: 0,
          paddingTop: isLoggedIn ? (navbarHeight > 0 ? 0 : "52px") : 0,
          marginLeft: isLoggedIn ? "1em" : 0,
          marginRight: isLoggedIn ? "1em" : 0,
        }}
      >
        {!commonStore.appLoaded ? (
          <LoadingComponent content="Loading..." />
        ) : (
          <Outlet />
        )}
      </div>
    </>
  );

  return (
    <HelmetProvider>
      <ToastContainer position="bottom-right" hideProgressBar />
      <ModalContainer />
      <Routes>
        {/* ── Truly public routes (no NavBar, never need it) ── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/account/reset-password" element={<ResetPasswordForm />} />
        <Route path="/server-error" element={<ServerError />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="*" element={<NotFound />} />

        {/* ── Layout route: NavBar is ALWAYS rendered here ─────────────
                    HomePage is inside this layout so that when a logged-in user
                    navigates to "/" the NavBar is already mounted — no flicker.
                    HomePage itself decides what to render:
                      • guest      → landing page
                      • logged in  → <Navigate to="/playground" replace />
                ───────────────────────────────────────────────────────────── */}
        <Route path="/" element={layoutElement}>
          <Route index element={<HomePage />} />
          <Route path="/games" element={<GameDashboard />} />
          <Route key={location.key} path="/createGame" element={<GameForm />} />
          <Route key={location.key} path="/manage/:id" element={<GameForm />} />
          <Route path="/games/:id" element={<GameDetails />} />
          <Route path="/profiles/:userID" element={<ProfilePage />} />
          <Route path="/venues" element={<VenueScheduler />} />
          <Route path="/friends" element={<FindFriends />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/playground" element={<PlaygroundPage />} />
          <Route path="/teams" element={<TeamDashboard />} />
          <Route path="/teams/:id" element={<TeamDetails />} />
          <Route path="/createTeam" element={<TeamForm />} />
          <Route path="/teams/:id/edit" element={<EditTeamWrapper />} />
          <Route path="/teams?tab=find" element={<TeamSearchPage />} />
          <Route path="/teammatches/:id" element={<TeamMatchPage />} />
          <Route path="/tournaments" element={<TournamentDashboard />} />
          <Route path="/tournaments/:id" element={<TournamentDetails />} />
          <Route path="/createTournament" element={<TournamentForm />} />
          <Route
            path="/tournaments/:id/edit"
            element={<EditTournamentWrapper />}
          />
          <Route
            path="/tournamentmatches/:id"
            element={<TournamentMatchPage />}
          />
        </Route>
      </Routes>
    </HelmetProvider>
  );
}

// ── Edit wrappers ─────────────────────────────────────────────────────────────

const EditTeamWrapper = observer(function EditTeamWrapper() {
  const { id } = useParams<{ id: string }>();
  const { teamStore } = useStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (id) teamStore.loadTeam(id);
  }, [id]);

  if (teamStore.loadingInitial || !teamStore.selectedTeam)
    return <LoadingComponent content={t("teams.loading")} />;

  if (!teamStore.selectedTeam.isCurrentUserCaptain)
    return <Navigate to={`/teams/${id}`} />;

  return <TeamForm team={teamStore.selectedTeam} />;
});

const EditTournamentWrapper = observer(function EditTournamentWrapper() {
  const { id } = useParams<{ id: string }>();
  const { tournamentStore } = useStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (id) tournamentStore.loadTournament(id);
  }, [id]);

  if (tournamentStore.loadingInitial || !tournamentStore.selectedTournament)
    return <LoadingComponent content={t("tournament.loading")} />;

  const tournament = tournamentStore.selectedTournament;

  if (
    !tournament.isCurrentUserOrganizer &&
    !tournament.isCurrentUserCoOrganizer
  )
    return <Navigate to={`/tournaments/${id}`} />;

  return <TournamentForm tournament={tournament} />;
});

export default observer(App);
